import {
  type Locale,
  getSessionLocalePick,
  getStoredLocale,
  initLocale,
  isLocale,
  localeFromPathname,
  rememberFooterLocalePick,
  setLocale as setI18nLocale,
  t,
} from './i18n/strings'
import { fetchGeoLocale } from './lib/geoLocale'
import {
  JOURNEY_STEP_IDS,
  noteJourneyStep,
  resetJourneyFarthest,
} from './lib/journeySoft'
import { decodeHandLink } from './lib/handLink'
import {
  actorSeat,
  deriveHandFlow,
  handEndsOnePlayerLeft,
  handEndsMemoryCut,
  formatBb,
  seatActor,
} from './lib/handFlow'
import { actorsMatch } from './lib/notation'
import {
  bbToChips,
  chipsToBb,
  createInitialState,
  getPositions,
  handHasDraftProgress,
  buildOpenThenCallOrderedPf,
  planFoldToHero,
  planOpenThenCall,
  pruneKnownHoles,
  sanitizeHand,
  uid,
} from './lib/poker'
import { sessionGet, sessionSet } from './lib/storageKeys'
import type {
  ActionType,
  Card,
  EndingPot,
  HandEnding,
  HandState,
  PickTarget,
  StackUnit,
  StepId,
  Street,
  StreetAction,
  ViewId,
} from './lib/types'

export type ToastKind =
  | null
  | 'copied'
  | 'shared'
  | 'shareFailed'
  | 'linkCopied'
  | 'linkWithoutKnownHoles'
  | 'linkWithoutEnding'
  | 'linkWithoutTakeaway'
  | 'linkTooLong'
  | 'actorRequired'
  | 'sizeRequired'
  | 'villainNudge'
  | 'seatFullName'
  | 'macroOpenExists'
  | 'macroAlreadyLogged'
  | 'macroFoldToHeroNone'
  | 'macroFoldToHeroBlocked'
  | 'imageSaved'
  | 'imageShared'
  | 'imageFailed'

/** Soft gate before summary/share when hand looks incomplete. */
export type SoftGateIntent =
  | 'summary'
  | 'share'
  | 'copy'
  | 'png-save'
  | 'png-share'
  | 'capture'
  | 'link'

export type SoftGatePhase = 'offer' | 'confirm-force'

export type SoftGate = {
  intent: SoftGateIntent
  /** When intent is copy, which payload mode to run after ack. */
  copyMode?: 'both' | 'human' | 'pro'
  /** offer = first prompt; confirm-force = second confirm before force-share. */
  phase?: SoftGatePhase
} | null

/** Action size snap-slider unit: big blinds or pot-fraction (Softwise M8). */
export type SizeSliderUnit = 'bb' | 'pct'

export type UiState = {
  view: ViewId
  step: StepId
  hand: HandState
  pickTarget: PickTarget
  actionStreet: Street
  actor: string
  /**
   * Softwise M10a: draft action type the user picked, or null when nothing is
   * picked yet. Never preselected — the app must not look like it already
   * answered (e.g. 「BB: 레이즈」) before the user taps a type chip.
   */
  actionType: ActionType | null
  actionSize: string
  /** Snap-slider unit for bet/raise size. */
  sizeSliderUnit: SizeSliderUnit
  /** When true, street changes do not auto-switch sizeSliderUnit. */
  sizeSliderUnitManual: boolean
  toast: ToastKind
  locale: Locale
  softGate: SoftGate
  /** When set, primary Add becomes Update for this action id. */
  editingActionId: string | null
  /**
   * Cards-step mode affordance only (not part of hand model).
   * heroOnly hides villain seat chips; withVillains reveals opponent entry.
   */
  holesMode: HolesMode
  /** Summary screenshot framing — hide chrome */
  captureMode: boolean
  /**
   * Soft I1b: the summary step shows only the share card + [되돌리기][공유]
   * (entered when input finishes). State only — never a route / query / hash.
   */
  summaryCard?: boolean
}

export type HolesMode = 'heroOnly' | 'withVillains'

type Listener = () => void

const DRAFT_STORAGE_SUFFIX = 'v1'
/** Session-wide successful Add count (not edits); drives sticky→full draft reset. */
const ADD_MASTERY_SUFFIX = 'add-mastery-count'
/** After this many successful Adds, reset actionType+size to street defaults. */
export const ADD_MASTERY_THRESHOLD = 2
/**
 * Session hand index (1-based). First hand of a tab session is 1.
 * Bumped on resetHand / New hand; unlock Line shop presets when ≥ 2.
 */
const SESSION_HAND_SUFFIX = 'session-hand-count'
/** In-memory fallback when sessionStorage is unavailable. */
let successfulAddCountMem = 0
/** First hand of session = 1 (unlock Line shop on 2nd hand). */
let sessionHandCountMem = 1
/** Soft-safe ⑥: farthestStep lives in journeySoft.ts (noteJourneyStep / reset). */
/** Last applied `#/h/<payload>` so we do not re-hydrate on every hash tick. */
let lastHandLinkPayload: string | null = null
/**
 * Soft D: `#/h/` received-hand summary — Quiet re-record CTA opens blank only.
 * Soft-safe ⑤ FAIL: must never auto-fill received content into the new record.
 * Not persisted (draft restore is not a received-link view).
 */
let receivedFromLink = false
const STEP_IDS: StepId[] = ['setup', ...JOURNEY_STEP_IDS]

// Soft-safe ⑥: journey Soft helpers live in journeySoft.ts — re-export for callers.
export {
  getFarthestStepIndex,
  heroHolesComplete,
  isNextSoftDisabled,
  isStepChipInJourney,
  unlockStepsThrough,
} from './lib/journeySoft'
const BOARD_PICK_TARGETS: PickTarget[] = [
  'flop0',
  'flop1',
  'flop2',
  'turn',
  'river',
]

const listeners = new Set<Listener>()

const SEAT_HOLE_RE = /^seat:([^:]+):([01])$/

export function parseSeatHoleTarget(
  target: string,
): { pos: string; slot: 0 | 1 } | null {
  const m = SEAT_HOLE_RE.exec(target)
  if (!m) return null
  return { pos: m[1]!, slot: Number(m[2]) as 0 | 1 }
}

export function makeSeatHoleTarget(pos: string, slot: 0 | 1): PickTarget {
  return `seat:${pos}:${slot}` as PickTarget
}

export function isValidPickTarget(value: string): value is PickTarget {
  if (
    value === 'hole0' ||
    value === 'hole1' ||
    BOARD_PICK_TARGETS.includes(value as PickTarget)
  ) {
    return true
  }
  return parseSeatHoleTarget(value) != null
}

export function isHoleTarget(target: PickTarget): boolean {
  return (
    target === 'hole0' ||
    target === 'hole1' ||
    parseSeatHoleTarget(target) != null
  )
}

export function isBoardTarget(target: PickTarget): boolean {
  return BOARD_PICK_TARGETS.includes(target)
}

/** Which cards-step seat the pick target belongs to (`hero` or position code). */
export function holeSeatFromTarget(target: PickTarget): 'hero' | string | null {
  if (target === 'hole0' || target === 'hole1') return 'hero'
  const parsed = parseSeatHoleTarget(target)
  return parsed ? parsed.pos : null
}

export function getHolePairForSeat(
  hand: HandState,
  seat: 'hero' | string,
): [Card | null, Card | null] {
  if (seat === 'hero') return hand.holeCards
  return hand.knownHoles?.[seat] ?? [null, null]
}

export function firstEmptyHoleTargetForSeat(
  hand: HandState,
  seat: 'hero' | string,
): PickTarget {
  if (seat === 'hero') {
    if (!hand.holeCards[0]) return 'hole0'
    if (!hand.holeCards[1]) return 'hole1'
    return 'hole0'
  }
  const pair = hand.knownHoles?.[seat] ?? [null, null]
  if (!pair[0]) return makeSeatHoleTarget(seat, 0)
  if (!pair[1]) return makeSeatHoleTarget(seat, 1)
  return makeSeatHoleTarget(seat, 0)
}

export function firstEmptyHoleTarget(hand: HandState): PickTarget {
  return firstEmptyHoleTargetForSeat(hand, 'hero')
}

export function firstEmptyBoardTarget(hand: HandState): PickTarget {
  if (!hand.board.flop[0]) return 'flop0'
  if (!hand.board.flop[1]) return 'flop1'
  if (!hand.board.flop[2]) return 'flop2'
  if (!hand.board.turn) return 'turn'
  if (!hand.board.river) return 'river'
  return 'flop0'
}

function seatTargetStillValid(hand: HandState, target: PickTarget): boolean {
  const seat = holeSeatFromTarget(target)
  if (!seat) return false
  if (seat === 'hero') return true
  const positions = getPositions(hand.playerCount)
  return positions.includes(seat) && seat !== hand.heroPosition
}

/**
 * On cards/board entry: first empty slot (hole0 / flop0 preferred).
 * If current already belongs to the step and all slots filled, keep it.
 */
export function pickTargetForStep(
  step: StepId,
  hand: HandState,
  current: PickTarget,
): PickTarget {
  if (step === 'cards') {
    if (isHoleTarget(current) && seatTargetStillValid(hand, current)) {
      const seat = holeSeatFromTarget(current)!
      const pair = getHolePairForSeat(hand, seat)
      if (pair[0] && pair[1]) return current
      return firstEmptyHoleTargetForSeat(hand, seat)
    }
    return firstEmptyHoleTarget(hand)
  }
  if (step === 'board') {
    const empty = firstEmptyBoardTarget(hand)
    const allFilled =
      Boolean(hand.board.flop[0]) &&
      Boolean(hand.board.flop[1]) &&
      Boolean(hand.board.flop[2]) &&
      Boolean(hand.board.turn) &&
      Boolean(hand.board.river)
    if (allFilled && isBoardTarget(current)) return current
    return empty
  }
  return current
}

/** Resolve a writable target for the current step, retargeting if stale. */
export function resolvePickTargetForAssign(
  step: StepId,
  hand: HandState,
  current: PickTarget,
): PickTarget | null {
  if (step === 'cards') {
    if (isHoleTarget(current) && seatTargetStillValid(hand, current)) {
      return current
    }
    return firstEmptyHoleTarget(hand)
  }
  if (step === 'board') {
    return isBoardTarget(current) ? current : firstEmptyBoardTarget(hand)
  }
  return null
}

/** Switch cards-step seat chip: Hero or a villain position. */
export function setCardsSeat(seat: 'hero' | string): void {
  if (seat !== 'hero') {
    const positions = getPositions(state.hand.playerCount)
    if (!positions.includes(seat) || seat === state.hand.heroPosition) return
  }
  const pickTarget = firstEmptyHoleTargetForSeat(state.hand, seat)
  const partial: Partial<UiState> = { pickTarget }
  // Tapping an opponent seat implies "with villains" mode.
  if (seat !== 'hero' && state.holesMode !== 'withVillains') {
    partial.holesMode = 'withVillains'
  }
  set(partial)
}

export function setHolesMode(mode: HolesMode): void {
  if (mode !== 'heroOnly' && mode !== 'withVillains') return
  if (mode === state.holesMode) return
  const partial: Partial<UiState> = { holesMode: mode }
  // Hero-only: jump focus back to hero seat if currently on a villain.
  if (mode === 'heroOnly') {
    const seat = holeSeatFromTarget(state.pickTarget)
    if (seat && seat !== 'hero') {
      partial.pickTarget = firstEmptyHoleTargetForSeat(state.hand, 'hero')
    }
  }
  set(partial)
}

export function areBlindsValid(hand: HandState): boolean {
  const { smallBlind: sb, bigBlind: bb } = hand
  return (
    Number.isFinite(bb) &&
    bb > 0 &&
    Number.isFinite(sb) &&
    sb >= 0 &&
    sb <= bb
  )
}

/** Which L1 gaps SoftGate can name (holes and/or actions). */
export function incompleteGaps(hand: HandState): {
  holes: boolean
  actions: boolean
} {
  return {
    holes: !hand.holeCards[0] || !hand.holeCards[1],
    actions: hand.actions.length === 0,
  }
}

/**
 * SoftGate open + Incomplete stamp.
 * Soft C1 Soft-safe Quiet — L1.0 holes-only honesty: hero holes present with
 * zero actions is a valid process record. Do NOT SoftGate-double-pressure
 * action-incomplete (Soft-safe ⑤ Quiet). SoftGate still opens when holes gap.
 */
export function handLooksIncomplete(hand: HandState): boolean {
  const gaps = incompleteGaps(hand)
  return gaps.holes
}

/** Parse a numeric field; empty → 0; invalid → null. */
export function parseBlindInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return 0
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return null
  return n
}

/** Parse `2.5bb` / `2bb` style size strings → number, else null. */
export function parseBbSize(raw: string): number | null {
  const m = raw.trim().match(/^(\d+(?:\.\d+)?)bb$/i)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) ? n : null
}

/** Format a BB amount for actionSize (trim trailing .0). */
export function formatBbSize(n: number): string {
  if (!Number.isFinite(n) || n < 0) return ''
  const rounded = Math.round(n * 100) / 100
  const s = Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(/\.?0+$/, '')
  return `${s}bb`
}

/**
 * Parse pot-fraction size strings (Softwise M8 Soft-refuse: no bare `%` labels).
 * Accepts process labels (`½ pot`, `⅓ pot`, `pot`, `1.5x`) and legacy `%` deep-links.
 */
export function parsePctSize(raw: string): number | null {
  const trimmed = raw.trim().toLowerCase().replace(/\s+/g, ' ')
  if (!trimmed) return null
  if (trimmed === 'pot' || trimmed === '100%') return 100
  if (trimmed === '2x' || trimmed === '200%') return 200
  if (trimmed === '1.5x' || trimmed === '150%') return 150
  if (trimmed === '1.25x' || trimmed === '125%') return 125
  // Unicode / ascii pot fractions (Softwise M8 process notation)
  const fracs: Array<[RegExp, number]> = [
    [/^(?:¼|1\/4) pot$/, 25],
    [/^(?:⅓|1\/3) pot$/, 33],
    [/^(?:½|1\/2) pot$/, 50],
    [/^(?:⅔|2\/3) pot$/, 66],
    [/^(?:¾|3\/4) pot$/, 75],
    [/^0\.1[x×] pot$/, 10],
    [/^0\.2[x×] pot$/, 20],
    [/^0\.4[x×] pot$/, 40],
  ]
  for (const [re, n] of fracs) {
    if (re.test(trimmed)) return n
  }
  const timesPot = trimmed.match(/^(\d+(?:\.\d+)?)[x×] pot$/)
  if (timesPot) {
    const n = Math.round(Number(timesPot[1]) * 100)
    return Number.isFinite(n) ? n : null
  }
  // Legacy `%` (deep-links / older drafts) — still parse, never display
  const m = trimmed.match(/^(\d+(?:\.\d+)?)%$/)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) ? n : null
}

/** Softwise M10b: bb on every street. */
export function defaultSizeSliderUnit(_street: Street): SizeSliderUnit {
  return 'bb'
}

function defaultUi(): UiState {
  return {
    view: 'app',
    // Softwise M10c: input starts with 'where did you sit?' (defaults apply).
    step: 'position',
    hand: createInitialState(),
    pickTarget: 'hole0',
    actionStreet: 'preflop',
    actor: 'Hero',
    // Softwise M10a: no prefilled action — actor may be preselected, type stays empty.
    actionType: null,
    actionSize: '2.5bb',
    sizeSliderUnit: 'bb',
    sizeSliderUnitManual: false,
    toast: null,
    locale: initLocale(),
    softGate: null,
    editingActionId: null,
    holesMode: 'heroOnly',
    captureMode: false,
  }
}

function loadPersisted(): Partial<UiState> | null {
  try {
    const raw = sessionGet(DRAFT_STORAGE_SUFFIX)
    if (!raw) return null
    const data = JSON.parse(raw) as Record<string, unknown>
    const hand = sanitizeHand(data.hand)
    if (!hand) return null
    // Softwise M10b: the separate board step is gone — board cards are asked
    // inline between streets on the Line step. Legacy `board` → `actions`.
    // Softwise M10c: setup is a side screen now — restore to the seat question.
    const rawStep =
      data.step === 'board' ? 'actions' : data.step === 'setup' ? 'position' : data.step
    const step =
      typeof rawStep === 'string' && STEP_IDS.includes(rawStep as StepId)
        ? (rawStep as StepId)
        : 'position'
    const pickTarget =
      typeof data.pickTarget === 'string' && isValidPickTarget(data.pickTarget)
        ? data.pickTarget
        : 'hole0'
    const actionStreet =
      data.actionStreet === 'flop' ||
      data.actionStreet === 'turn' ||
      data.actionStreet === 'river' ||
      data.actionStreet === 'preflop'
        ? data.actionStreet
        : 'preflop'
    // Softwise M10a: never restore a preselected draft type (legacy sessions
    // persisted 'raise' as a silent default). The user re-picks on return.
    const actionType: ActionType | null = null
    // Legacy sizePickMode/metaPickMode ignored (snap slider replaced approx/precise UI).
    const sizeSliderUnit: SizeSliderUnit =
      data.sizeSliderUnit === 'pct' || data.sizeSliderUnit === 'bb'
        ? data.sizeSliderUnit
        : defaultSizeSliderUnit(actionStreet)
    const sizeSliderUnitManual = data.sizeSliderUnitManual === true
    const holesMode: HolesMode =
      data.holesMode === 'withVillains' || data.holesMode === 'heroOnly'
        ? data.holesMode
        : 'heroOnly'
    return {
      hand,
      step,
      pickTarget,
      actionStreet,
      actor: typeof data.actor === 'string' ? data.actor : 'Hero',
      actionType,
      actionSize:
        typeof data.actionSize === 'string' ? data.actionSize : '2.5bb',
      sizeSliderUnit,
      sizeSliderUnitManual,
      holesMode,
    }
  } catch {
    return null
  }
}

function persist(s: UiState): void {
  try {
    const payload = {
      hand: s.hand,
      step: s.step,
      pickTarget: s.pickTarget,
      actionStreet: s.actionStreet,
      actor: s.actor,
      actionType: s.actionType,
      actionSize: s.actionSize,
      sizeSliderUnit: s.sizeSliderUnit,
      sizeSliderUnitManual: s.sizeSliderUnitManual,
      holesMode: s.holesMode,
    }
    sessionSet(DRAFT_STORAGE_SUFFIX, JSON.stringify(payload))
  } catch {
    // quota / private mode — ignore
  }
}

const _persisted = loadPersisted()
let state: UiState = {
  ...defaultUi(),
  ..._persisted,
  toast: null,
  softGate: null,
  editingActionId: null,
  locale: initLocale(),
}
/** Full-page load restored a non-empty draft — show Continue vs New hand once. */
let draftRestorePrompt = Boolean(
  _persisted?.hand && handHasDraftProgress(_persisted.hand),
)

export function isDraftRestorePrompt(): boolean {
  return draftRestorePrompt
}

/** Soft D: true while viewing a hand hydrated from `#/h/` (until New / re-record). */
export function isReceivedFromLink(): boolean {
  return receivedFromLink
}

export function dismissDraftRestorePrompt(): void {
  if (!draftRestorePrompt) return
  draftRestorePrompt = false
  emit()
}

// Retarget stale pickTarget after hydrate (e.g. hole target while on board)
if (state.step === 'cards' || state.step === 'board') {
  state = {
    ...state,
    pickTarget: pickTargetForStep(state.step, state.hand, state.pickTarget),
  }
}
// Soft stepwise M2: draft restore opens journey through the restored step
noteJourneyStep(state.step)
// heroOnly: keep knownHoles data but focus hero seat (villain chips are hidden)
if (state.holesMode === 'heroOnly') {
  const seat = holeSeatFromTarget(state.pickTarget)
  if (seat && seat !== 'hero') {
    state = {
      ...state,
      pickTarget: firstEmptyHoleTargetForSeat(state.hand, 'hero'),
    }
  }
}

function applyDocumentLocale(locale: Locale): void {
  try {
    const root = document.documentElement
    root.lang = locale
    // All supported locales are LTR; set explicitly for assistive tech / layout.
    root.dir = 'ltr'
    const strings = t()
    document.title = `${strings.brand} — ${strings.docTitleSuffix}`
    const desc = strings.metaDescription
    const setMeta = (selector: string, attr: 'content', value: string) => {
      const el = document.querySelector(selector)
      if (el) el.setAttribute(attr, value)
    }
    setMeta('meta[name="description"]', 'content', desc)
    setMeta('meta[property="og:title"]', 'content', `${strings.brand} — ${strings.docTitleSuffix}`)
    setMeta('meta[property="og:description"]', 'content', desc)
    setMeta('meta[name="twitter:title"]', 'content', `${strings.brand} — ${strings.docTitleSuffix}`)
    setMeta('meta[name="twitter:description"]', 'content', desc)
  } catch {
    // ignore
  }
}

applyDocumentLocale(state.locale)

let toastTimer: number | undefined
/** Dynamic toast body (e.g. seat full-name decode). Cleared with toast. */
let toastDetailMemory: string | null = null

export function peekToastDetail(): string | null {
  return toastDetailMemory
}

export function getState(): UiState {
  return state
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Force a UI re-render without mutating hand draft. */
export function requestRender(): void {
  emit()
}

function emit(): void {
  persist(state)
  for (const listener of listeners) listener()
}

function set(partial: Partial<UiState>): void {
  // Soft I1a: every change to the entered input (setup, seat, holes, board,
  // actions — never the takeaway text or the ending record) pushes one undo
  // snapshot of the screen before it. A pending 「여기부터 다시」 is cancelled
  // by any other input change (nothing dimmed is ever kept behind the user).
  if (partial.hand && partial.hand !== state.hand && handInputKey(partial.hand) !== handInputKey(state.hand)) {
    if (!undoSuspended) pushUndoSnapshot()
    if (!rewindConfirming) rewindPending = null
  }
  state = { ...state, ...partial }
  emit()
}

/* ------------------------------------------------------------------ Soft I1a
 * Undo snapshot stack (input state only) + pending tap-rewind.
 *
 * - Snapshots hold the hand and where the user was (step / pick target /
 *   street / actor). Undo restores them in one tap: no confirm, no toast.
 * - The takeaway text is never rolled back (always the current text).
 * - Ending record (same rule as 「여기부터 다시」): if the undo changes the
 *   line's street (deriveHandFlow().street before vs after), the ending is
 *   cleared; otherwise it stays exactly as it is now (an ending the user
 *   cleared stays cleared). The ONLY refill: undoing the confirmRewind step
 *   that itself cleared the ending (snapshot flag `endingClearedByRewind`).
 * - Session memory only: the Draft (sessionStorage) always holds the current
 *   hand, the stack is not persisted, shares / `#/h/` / PNG never carry it.
 */
type UndoSnapshot = {
  hand: HandState
  step: StepId
  pickTarget: PickTarget
  actionStreet: Street
  actor: string
  heroSeatPicked: boolean
  /** Set on the snapshot pushed by a confirmRewind that cleared the ending. */
  endingClearedByRewind?: boolean
}
export const UNDO_MAX = 50
let undoStack: UndoSnapshot[] = []
let undoSuspended = false
let rewindConfirming = false

/** Input identity of a hand: everything except the free text and the ending record. */
function handInputKey(hand: HandState): string {
  return JSON.stringify({ ...hand, takeaway: undefined, ending: undefined })
}

function pushUndoSnapshot(): void {
  undoStack.push({
    hand: structuredClone(state.hand),
    step: state.step,
    pickTarget: state.pickTarget,
    actionStreet: state.actionStreet,
    actor: state.actor,
    heroSeatPicked,
  })
  if (undoStack.length > UNDO_MAX) undoStack = undoStack.slice(-UNDO_MAX)
}

export function canUndo(): boolean {
  return undoStack.length > 0
}

export function undoDepth(): number {
  return undoStack.length
}

function clearUndo(): void {
  undoStack = []
  rewindPending = null
}

/**
 * One tap: put the input back to the previous snapshot. A pending
 * 「여기부터 다시」 is simply cancelled first (nothing was removed yet).
 * Returns false when there is nothing to undo.
 */
export function undoLast(): boolean {
  if (rewindPending) {
    cancelRewind()
    return true
  }
  const snap = undoStack.pop()
  if (!snap) return false
  const cur = state.hand
  const hand: HandState = { ...snap.hand, takeaway: cur.takeaway }
  let ending: HandState['ending']
  if (snap.endingClearedByRewind) {
    // Undoing the rewind that cleared it: that exact record comes back.
    ending = snap.hand.ending
  } else if (deriveHandFlow(hand).street !== deriveHandFlow(cur).street) {
    // Undo moved the line to another street: clear, never auto-refill.
    ending = undefined
  } else {
    ending = cur.ending
  }
  if (ending === undefined) delete hand.ending
  else hand.ending = ending
  heroSeatPicked = snap.heroSeatPicked
  if (snap.step !== state.step) draftRestorePrompt = false
  noteJourneyStep(snap.step)
  undoSuspended = true
  try {
    set({
      hand,
      step: snap.step,
      pickTarget: snap.pickTarget,
      actionStreet: snap.actionStreet,
      actor: snap.actor,
      actionType: null,
      editingActionId: null,
      softGate: null,
      captureMode: false,
    })
  } finally {
    undoSuspended = false
  }
  return true
}

/** Strip target for tap-rewind (same shape rewindLineTo takes). */
export type RewindTarget = { actionId: string } | { street: 'flop' | 'turn' | 'river' }
let rewindPending: RewindTarget | null = null

export function getRewindPending(): RewindTarget | null {
  return rewindPending
}

/** Tap a past strip item: dim it and everything after it. Nothing is removed yet. */
export function beginRewind(target: RewindTarget): void {
  const hand = state.hand
  if ('actionId' in target && !hand.actions.some((a) => a.id === target.actionId)) return
  if ('street' in target) {
    const has = target.street === 'flop' ? hand.board.flop.some(Boolean) : Boolean(hand.board[target.street])
    if (!has) return
  }
  rewindPending = target
  emit()
}

/** Cancel: the dimmed items come back exactly as they were. */
export function cancelRewind(): void {
  if (!rewindPending) return
  rewindPending = null
  emit()
}

/**
 * 「여기부터 다시」 confirm: only now the dimmed items are removed (one undo
 * snapshot). If the line's street changes, the ending record is cleared and
 * never refilled.
 */
export function confirmRewind(): boolean {
  const target = rewindPending
  if (!target) return false
  const beforeStreet = deriveHandFlow(state.hand).street
  rewindConfirming = true
  try {
    rewindLineTo(target)
  } finally {
    rewindConfirming = false
  }
  rewindPending = null
  const afterStreet = deriveHandFlow(state.hand).street
  if (afterStreet !== beforeStreet && state.hand.ending !== undefined) {
    const { ending: _drop, ...rest } = state.hand
    void _drop
    // Undoing this rewind (and only this) may bring the cleared ending back.
    const top = undoStack[undoStack.length - 1]
    if (top) top.endingClearedByRewind = true
    undoSuspended = true
    try {
      set({ hand: rest as HandState })
    } finally {
      undoSuspended = false
    }
  } else {
    emit()
  }
  return true
}

export function setView(view: ViewId): void {
  set({ view })
}

export function setStep(step: StepId, opts: { card?: boolean } = {}): void {
  // Soft I1a: dimmed (pending-rewind) items never reach the share card.
  if (rewindPending && step === 'summary') return
  // Soft I1b: input finished → summary card view (no toast, no gate).
  const summaryCard = step === 'summary' && opts.card === true
  if (summaryCard) {
    if (toastTimer !== undefined) window.clearTimeout(toastTimer)
    toastTimer = undefined
    toastDetailMemory = null
  }
  if (rewindPending && step !== state.step) rewindPending = null
  const pickTarget = pickTargetForStep(step, state.hand, state.pickTarget)
  // Always clear soft gate on navigation (ack path clears then setSteps)
  const captureMode = step === 'summary' ? state.captureMode : false
  noteJourneyStep(step)
  // Softwise M10d: the fresh-load resume offer goes away once the user moves
  // on (screen change) — it never lingers into, or appears during, input.
  if (step !== state.step) draftRestorePrompt = false
  set({ step, pickTarget, softGate: null, captureMode, summaryCard, ...(summaryCard ? { toast: null } : {}) })
}

/** Soft I1b: 「공유」 under the summary card → the full share screen (same step). */
export function openShareFromSummaryCard(): void {
  if (state.step !== 'summary' || !state.summaryCard) return
  set({ summaryCard: false })
}

/** Soft I1b: 「되돌리기」 under the summary card → back to the line, nothing removed. */
export function backFromSummaryCard(): void {
  if (state.step !== 'summary' || !state.summaryCard) return
  setStep('actions')
}

export function isSummaryCard(): boolean {
  return state.step === 'summary' && state.summaryCard === true
}

/** Soft stepwise: session hand index (1-based; first hand = 1). */
export function getSessionHandIndex(): number {
  return readSessionHandCount()
}

export function setPickTarget(pickTarget: PickTarget): void {
  if (state.step === 'cards' && !isHoleTarget(pickTarget)) return
  if (state.step === 'board' && !isBoardTarget(pickTarget)) return
  set({ pickTarget })
}

/**
 * Softwise M10b: sizes are recorded in bb on every street (no pot-fraction
 * favorite). Kept as a no-op so legacy callers stay harmless.
 */
export function notePostflopSizeFavorite(_size: string): void {}

/**
 * Default type/size when switching streets (or after add).
 * Actor is chosen separately so street tabs can keep the current actor.
 */
function defaultDraftForStreet(street: Street): {
  actionType: ActionType | null
  actionSize: string
} {
  // Softwise M10a: street default seeds size only; type stays unpicked (null)
  // so the draft never reads as a prefilled 「raise」/「bet」 line.
  // Softwise M10b: bb on every street (postflop too) — no pot-fraction seed.
  void street
  return { actionType: null, actionSize: '2.5bb' }
}

function readSuccessfulAddCount(): number {
  const raw = sessionGet(ADD_MASTERY_SUFFIX)
  if (raw != null) {
    const n = Number.parseInt(raw, 10)
    if (Number.isFinite(n) && n >= 0) {
      successfulAddCountMem = n
      return n
    }
  }
  return successfulAddCountMem
}

/** Bump session mastery on a successful *new* Add (not edits). Returns count including this add. */
function bumpSuccessfulAddCount(): number {
  const next = readSuccessfulAddCount() + 1
  successfulAddCountMem = next
  sessionSet(ADD_MASTERY_SUFFIX, String(next))
  return next
}

function readSessionHandCount(): number {
  const raw = sessionGet(SESSION_HAND_SUFFIX)
  if (raw != null) {
    const n = Number.parseInt(raw, 10)
    if (Number.isFinite(n) && n >= 1) {
      sessionHandCountMem = n
      return n
    }
  }
  return sessionHandCountMem
}

/** Bump on New hand / resetHand. Returns the hand index for the hand now starting. */
function bumpSessionHandCount(): number {
  const next = readSessionHandCount() + 1
  sessionHandCountMem = next
  sessionSet(SESSION_HAND_SUFFIX, String(next))
  return next
}

/**
 * Progressive-mastery Line shop (Presets · size chevron).
 * Locked on the session's first hand while successful Adds < 2 and
 * current hand actions < 2. Unlocks when any of:
 *   - session hand index ≥ 2 (after New hand once; first hand = 1)
 *   - successful Adds ≥ ADD_MASTERY_THRESHOLD (2)
 *   - current hand actions ≥ 2
 * Reuses add-mastery-count; hand index is separate (session-hand-count).
 */
export function isLineShopUnlocked(handActionCount: number): boolean {
  return (
    readSessionHandCount() >= 2 ||
    readSuccessfulAddCount() >= ADD_MASTERY_THRESHOLD ||
    handActionCount >= 2
  )
}

/**
 * Draft after a successful Add.
 * Softwise M10a: the action type is always cleared (null) — no sticky
 * 「raise」 and no safe-continue 「call/check」 prefill. Actor still soft-advances
 * (whose turn is next is OK); only the user picks what they did.
 * Size resets to the street default so picking bet/raise never hits sizeRequired.
 * Session Add count (ADD_MASTERY_THRESHOLD) still drives Line shop unlock.
 */
export function draftAfterSuccessfulAdd(
  street: Street,
  _stickyActionType: ActionType,
  _successfulAddsIncludingThis: number,
): { actionType: ActionType | null; actionSize: string } {
  const streetDefault = defaultDraftForStreet(street)
  return { actionType: null, actionSize: streetDefault.actionSize }
}

/** Valid chip actors for the current table (Hero + non-hero seats). */
export function validActorChips(hand: HandState): string[] {
  const positions = getPositions(hand.playerCount)
  return ['Hero', ...positions.filter((p) => p !== hand.heroPosition)]
}

export function setActionStreet(actionStreet: Street): void {
  const draft = defaultDraftForStreet(actionStreet)
  const chips = validActorChips(state.hand)
  const folded = foldedActorsThroughStreet(state.hand, actionStreet)
  // Keep current actor across streets when still a valid non-folded chip.
  let keepActor = chips.includes(state.actor) ? state.actor : 'Hero'
  if (folded.has(keepActor)) {
    keepActor = nextActorAfterAdd(keepActor, state.hand, actionStreet)
    if (folded.has(keepActor)) {
      keepActor = chips.find((c) => !folded.has(c)) ?? 'Hero'
    }
  }
  const partial: Partial<UiState> = {
    actionStreet,
    actor: keepActor,
    actionType: draft.actionType,
    actionSize: draft.actionSize,
    editingActionId: null,
  }
  if (!state.sizeSliderUnitManual) {
    partial.sizeSliderUnit = defaultSizeSliderUnit(actionStreet)
  }
  set(partial)
}

export function setActor(actor: string, opts?: { silent?: boolean }): void {
  if (opts?.silent) {
    state = { ...state, actor }
    persist(state)
    return
  }
  set({ actor })
}

export function setActionType(actionType: ActionType | null): void {
  // Softwise M10a: tapping the active type chip clears it (no forced answer).
  if (actionType === null || actionType === state.actionType) {
    set({ actionType: null })
    return
  }
  const sized = actionType === 'bet' || actionType === 'raise'
  if (sized && !state.actionSize.trim()) {
    set({
      actionType,
      actionSize: defaultDraftForStreet(state.actionStreet).actionSize,
    })
    return
  }
  set({ actionType })
}

export function setActionSize(
  actionSize: string,
  opts?: { silent?: boolean; unit?: SizeSliderUnit },
): void {
  const partial: Partial<UiState> = { actionSize }
  if (opts?.unit === 'bb' || opts?.unit === 'pct') {
    partial.sizeSliderUnit = opts.unit
  }
  notePostflopSizeFavorite(actionSize)
  if (opts?.silent) {
    state = { ...state, ...partial }
    persist(state)
    return
  }
  set(partial)
}


export function setSizeSliderUnit(
  sizeSliderUnit: SizeSliderUnit,
  opts?: { manual?: boolean; actionSize?: string },
): void {
  if (sizeSliderUnit !== 'bb' && sizeSliderUnit !== 'pct') return
  const partial: Partial<UiState> = {
    sizeSliderUnit,
    sizeSliderUnitManual: opts?.manual
      ? true
      : state.sizeSliderUnitManual,
  }
  if (opts?.actionSize != null) partial.actionSize = opts.actionSize
  set(partial)
}

export function setAppLocale(locale: Locale): void {
  // Footer locale PR: the header select is a newer explicit pick — kept as the
  // session pick too, so a reload on /ko does not fall back to the path locale.
  rememberFooterLocalePick(locale)
  setI18nLocale(locale)
  applyDocumentLocale(locale)
  set({ locale })
}

export function setCaptureMode(captureMode: boolean): void {
  set({ captureMode: !!captureMode })
}

export function patchHand(updater: (prev: HandState) => HandState): void {
  set({ hand: updater(state.hand) })
}

export function setPlayerCount(playerCount: number): void {
  const positions = getPositions(playerCount)
  patchHand((prev) => {
    const heroPosition = positions.includes(prev.heroPosition)
      ? prev.heroPosition
      : positions[positions.length - 1]!
    return {
      ...prev,
      playerCount,
      heroPosition,
      knownHoles: pruneKnownHoles(prev.knownHoles, playerCount, heroPosition),
    }
  })
  // Retarget if current seat hole no longer valid
  if (state.step === 'cards' && !seatTargetStillValid(state.hand, state.pickTarget)) {
    set({ pickTarget: firstEmptyHoleTarget(state.hand) })
  }
}

/**
 * Softwise M10c: seat tap = answer + advance (no Next). Holes still missing →
 * hole cards; holes already picked (editing the seat from the line) → Line.
 */
let heroSeatPicked = false
export function isHeroSeatPicked(): boolean {
  return heroSeatPicked || (state.step !== 'position' && state.step !== 'setup') || Boolean(state.hand.holeCards[0] || state.hand.holeCards[1])
}
export function pickHeroSeat(heroPosition: string): void {
  setHeroPosition(heroPosition)
  heroSeatPicked = true
  const holesDone = Boolean(state.hand.holeCards[0] && state.hand.holeCards[1])
  setStep(holesDone ? 'actions' : 'cards')
}

/** Softwise M10c: settings side screen (table · blinds · stack) and back. */
let settingsReturnStep: StepId = 'position'
export function openSettings(): void {
  if (state.step !== 'setup') settingsReturnStep = state.step
  setStep('setup')
}
export function closeSettings(): void {
  if (!areBlindsValid(state.hand)) return
  setStep(settingsReturnStep)
}

export function setHeroPosition(heroPosition: string): void {
  patchHand((prev) => ({
    ...prev,
    heroPosition,
    knownHoles: pruneKnownHoles(prev.knownHoles, prev.playerCount, heroPosition),
  }))
  if (state.step === 'cards' && !seatTargetStillValid(state.hand, state.pickTarget)) {
    set({ pickTarget: firstEmptyHoleTarget(state.hand) })
  }
}

export function setStackUnit(stackUnit: StackUnit): void {
  patchHand((prev) => {
    if (stackUnit === prev.stackUnit) return prev
    if (stackUnit === 'chips') {
      return {
        ...prev,
        stackUnit,
        heroStackChips: bbToChips(prev.heroStackBb, prev.bigBlind),
      }
    }
    return {
      ...prev,
      stackUnit,
      heroStackBb: chipsToBb(prev.heroStackChips, prev.bigBlind),
    }
  })
}

export function setHeroStackBb(heroStackBb: number): void {
  const value =
    Number.isFinite(heroStackBb) && heroStackBb >= 0 ? heroStackBb : 0
  patchHand((prev) => ({
    ...prev,
    heroStackBb: value,
    heroStackChips: bbToChips(value, prev.bigBlind),
  }))
}

export function setHeroStackChips(heroStackChips: number): void {
  const value =
    Number.isFinite(heroStackChips) && heroStackChips >= 0 ? heroStackChips : 0
  patchHand((prev) => ({
    ...prev,
    heroStackChips: value,
    heroStackBb: chipsToBb(value, prev.bigBlind),
  }))
}

export function setBlinds(partial: {
  smallBlind?: number | null
  bigBlind?: number | null
}): void {
  patchHand((prev) => {
    let smallBlind = prev.smallBlind
    let bigBlind = prev.bigBlind
    if ('smallBlind' in partial) {
      const v = partial.smallBlind
      smallBlind =
        v != null && Number.isFinite(v) ? Math.max(0, v) : Number.NaN
    }
    if ('bigBlind' in partial) {
      const v = partial.bigBlind
      // Invalid/empty BB → NaN so areBlindsValid fails (BB must be > 0)
      bigBlind = v != null && Number.isFinite(v) ? Math.max(0, v) : Number.NaN
    }
    const next = { ...prev, smallBlind, bigBlind }
    const bbForStack = Number.isFinite(bigBlind) && bigBlind > 0 ? bigBlind : 0
    if (next.stackUnit === 'chips') {
      next.heroStackBb = chipsToBb(next.heroStackChips, bbForStack)
    } else {
      next.heroStackChips = bbToChips(next.heroStackBb, bbForStack)
    }
    return next
  })
}

function clearOptionalNumber(
  prev: HandState,
  key: 'ante' | 'effectiveStackBb' | 'potBb',
): HandState {
  const next = { ...prev }
  delete next[key]
  return next
}

export function setAnte(ante: number | null): void {
  patchHand((prev) => {
    if (ante == null || !Number.isFinite(ante) || ante < 0) {
      return clearOptionalNumber(prev, 'ante')
    }
    return { ...prev, ante }
  })
}

export function setEffectiveStackBb(effectiveStackBb: number | null): void {
  patchHand((prev) => {
    if (
      effectiveStackBb == null ||
      !Number.isFinite(effectiveStackBb) ||
      effectiveStackBb < 0
    ) {
      return clearOptionalNumber(prev, 'effectiveStackBb')
    }
    return { ...prev, effectiveStackBb }
  })
}

export function setPotBb(potBb: number | null): void {
  patchHand((prev) => {
    if (potBb == null || !Number.isFinite(potBb) || potBb < 0) {
      return clearOptionalNumber(prev, 'potBb')
    }
    return { ...prev, potBb }
  })
}

/** Manual one-line takeaway — empty clears. Silent skips re-render (caret). */
export function setTakeaway(
  takeaway: string,
  opts?: { silent?: boolean },
): void {
  const next = takeaway.replace(/[\r\n]+/g, ' ').slice(0, 160)
  if (opts?.silent) {
    state = { ...state, hand: { ...state.hand, takeaway: next } }
    persist(state)
    return
  }
  patchHand((prev) => ({ ...prev, takeaway: next }))
}

// ---------------------------------------------------------------------------
// Soft G: optional ending record — press-only. Every setter toggles: pressing
// the pressed value again clears it. Nothing here ever picks a value for the
// user (no auto-fill even when everyone folds, no default, no preselection).
// ---------------------------------------------------------------------------
function withEnding(prev: HandState, next: HandEnding): HandState {
  const out: HandState = { ...prev }
  const shown = Object.fromEntries(
    Object.entries(next.shown ?? {}).filter(([, m]) => m != null),
  ) as NonNullable<HandEnding['shown']>
  const ending: HandEnding = {}
  if (Object.keys(shown).length) ending.shown = shown
  if (next.pot) ending.pot = next.pot
  if (ending.shown || ending.pot) out.ending = ending
  else delete out.ending
  return out
}

/** 「안 보여줌(머크)」 / 「모름」 for a seat (toggle). */
export function setEndingShownMark(seat: string, mark: 'muck' | 'unknown'): void {
  patchHand((prev) => {
    const cur = prev.ending?.shown?.[seat]
    const shown = { ...(prev.ending?.shown ?? {}) }
    if (cur === mark) delete shown[seat]
    else shown[seat] = mark
    return withEnding(prev, { ...prev.ending, shown })
  })
}

/** Entered showdown card for a seat — fills the first empty slot. */
export function addEndingShownCard(seat: string, card: Card): void {
  patchHand((prev) => {
    const cur = prev.ending?.shown?.[seat]
    const pair: [Card | null, Card | null] = Array.isArray(cur) ? [cur[0], cur[1]] : [null, null]
    if (pair[0] && pair[1]) return prev
    if (!pair[0]) pair[0] = card
    else pair[1] = card
    return withEnding(prev, { ...prev.ending, shown: { ...(prev.ending?.shown ?? {}), [seat]: pair } })
  })
}

/** Clear a seat's showdown mark (cards / muck / unknown). */
export function clearEndingShown(seat: string): void {
  patchHand((prev) => {
    const shown = { ...(prev.ending?.shown ?? {}) }
    delete shown[seat]
    return withEnding(prev, { ...prev.ending, shown })
  })
}

/** 「<seat> 팟 가져감」 / 「나눔」 / 「모름」 (toggle). */
export function setEndingPot(pot: EndingPot): void {
  patchHand((prev) => {
    const cur = prev.ending?.pot
    const same =
      cur != null &&
      cur.kind === pot.kind &&
      (pot.kind !== 'seat' || (cur.kind === 'seat' && cur.seat === pot.seat))
    return withEnding(prev, { ...prev.ending, pot: same ? undefined : pot })
  })
}

export function moveAction(id: string, dir: -1 | 1): void {
  patchHand((prev) => {
    const idx = prev.actions.findIndex((a) => a.id === id)
    if (idx < 0) return prev
    const street = prev.actions[idx]!.street
    const streetIdxs = prev.actions
      .map((a, i) => (a.street === street ? i : -1))
      .filter((i) => i >= 0)
    const pos = streetIdxs.indexOf(idx)
    const swapWith = streetIdxs[pos + dir]
    if (swapWith === undefined) return prev
    const actions = [...prev.actions]
    const tmp = actions[idx]!
    actions[idx] = actions[swapWith]!
    actions[swapWith] = tmp
    return { ...prev, actions }
  })
}

export function loadHandFromLink(hand: HandState, step: StepId = 'summary'): void {
  noteJourneyStep(step)
  // Soft D: mark received `#/h/` view — Quiet CTA opens blank (resetHand), never imports.
  receivedFromLink = true
  clearUndo()
  state = {
    ...state,
    hand,
    step,
    pickTarget: pickTargetForStep(step, hand, state.pickTarget),
    softGate: null,
    toast: null,
    view: 'app',
  }
  emit()
}

export function assignCard(card: Card, used: Set<Card>): void {
  if (used.has(card)) return
  const target = resolvePickTargetForAssign(
    state.step,
    state.hand,
    state.pickTarget,
  )
  if (!target) return

  // Advance only within the same step's slot group
  let nextTarget: PickTarget = target
  if (state.step === 'cards') {
    if (target === 'hole0') nextTarget = 'hole1'
    else {
      const seat = parseSeatHoleTarget(target)
      if (seat && seat.slot === 0) nextTarget = makeSeatHoleTarget(seat.pos, 1)
    }
  } else if (state.step === 'board') {
    if (target === 'flop0') nextTarget = 'flop1'
    else if (target === 'flop1') nextTarget = 'flop2'
    else if (target === 'flop2') nextTarget = 'turn'
    else if (target === 'turn') nextTarget = 'river'
  }

  // Ensure we never write hole while on board (and vice versa)
  if (state.step === 'cards' && !isHoleTarget(target)) return
  if (state.step === 'board' && !isBoardTarget(target)) return

  patchHand((prev) => {
    const next: HandState = {
      ...prev,
      holeCards: [...prev.holeCards] as HandState['holeCards'],
      knownHoles: { ...(prev.knownHoles ?? {}) },
      board: {
        ...prev.board,
        flop: [...prev.board.flop] as HandState['board']['flop'],
      },
    }
    const seat = parseSeatHoleTarget(target)
    if (target === 'hole0') {
      next.holeCards[0] = card
    } else if (target === 'hole1') {
      next.holeCards[1] = card
    } else if (seat) {
      const prevPair = next.knownHoles[seat.pos] ?? [null, null]
      const pair: [Card | null, Card | null] = [prevPair[0], prevPair[1]]
      pair[seat.slot] = card
      next.knownHoles[seat.pos] = pair
    } else if (target === 'flop0') {
      next.board.flop[0] = card
    } else if (target === 'flop1') {
      next.board.flop[1] = card
    } else if (target === 'flop2') {
      next.board.flop[2] = card
    } else if (target === 'turn') {
      next.board.turn = card
    } else if (target === 'river') {
      next.board.river = card
    }
    return next
  })

  set({ pickTarget: nextTarget })

  // Softwise M10c: the second hero hole card auto-advances to the Line
  // (no filled Next).
  if (
    state.step === 'cards' &&
    state.holesMode === 'heroOnly' &&
    (target === 'hole0' || target === 'hole1') &&
    state.hand.holeCards[0] &&
    state.hand.holeCards[1]
  ) {
    setStep('actions')
    return
  }

  // Softwise M10e: finishing a villain's second hole returns to the share
  // card when the line already exists (optional 「상대도 입력」 path).
  if (state.step === 'cards' && state.holesMode === 'withVillains') {
    const seat = parseSeatHoleTarget(target)
    if (
      seat &&
      state.hand.holeCards[0] &&
      state.hand.holeCards[1] &&
      state.hand.actions.length > 0
    ) {
      const pair = state.hand.knownHoles?.[seat.pos]
      if (pair?.[0] && pair?.[1]) {
        setStep('summary')
        return
      }
    }
  }

  // Soft discoverability nudge once hero holes are complete.
  if (
    state.step === 'cards' &&
    (target === 'hole0' || target === 'hole1') &&
    state.hand.holeCards[0] &&
    state.hand.holeCards[1]
  ) {
    maybeShowVillainNudge()
  }
}

const VILLAIN_NUDGE_SUFFIX = 'villain-nudge-seen'
let villainNudgeShownMemory = false

function maybeShowVillainNudge(): void {
  // Banner CTA covers heroOnly; toast only when opponent seats are already visible.
  if (state.holesMode !== 'withVillains') return
  if (villainNudgeShownMemory) return
  try {
    if (sessionGet(VILLAIN_NUDGE_SUFFIX) === '1') {
      villainNudgeShownMemory = true
      return
    }
    sessionSet(VILLAIN_NUDGE_SUFFIX, '1')
  } catch {
    // private mode — memory flag still prevents spam this session
  }
  villainNudgeShownMemory = true
  showToast('villainNudge')
}

export function clearSlot(target: PickTarget): void {
  if (state.step === 'cards' && !isHoleTarget(target)) return
  if (state.step === 'board' && !isBoardTarget(target)) return
  patchHand((prev) => {
    const next: HandState = {
      ...prev,
      holeCards: [...prev.holeCards] as HandState['holeCards'],
      knownHoles: { ...(prev.knownHoles ?? {}) },
      board: {
        ...prev.board,
        flop: [...prev.board.flop] as HandState['board']['flop'],
      },
    }
    const seat = parseSeatHoleTarget(target)
    if (target === 'hole0') next.holeCards[0] = null
    else if (target === 'hole1') next.holeCards[1] = null
    else if (seat) {
      const prevPair = next.knownHoles[seat.pos] ?? [null, null]
      const pair: [Card | null, Card | null] = [prevPair[0], prevPair[1]]
      pair[seat.slot] = null
      if (!pair[0] && !pair[1]) delete next.knownHoles[seat.pos]
      else next.knownHoles[seat.pos] = pair
    } else if (target === 'flop0') next.board.flop[0] = null
    else if (target === 'flop1') next.board.flop[1] = null
    else if (target === 'flop2') next.board.flop[2] = null
    else if (target === 'turn') next.board.turn = null
    else if (target === 'river') next.board.river = null
    return next
  })
  set({ pickTarget: target })
}

/** Street order for fold-through checks (once folded, out for the hand). */
const STREET_RANK: Record<Street, number> = {
  preflop: 0,
  flop: 1,
  turn: 2,
  river: 3,
}

/**
 * Chip actors folded on any street up to and including `street`.
 * Maps Hero ↔ heroPosition via actorsMatch so a PF fold stays out on flop+.
 * Pure helper — covered by scripts/test-fold-actor.mjs (+ typecheck).
 */
export function foldedActorsThroughStreet(
  hand: HandState,
  street: Street,
): Set<string> {
  const chips = validActorChips(hand)
  const maxRank = STREET_RANK[street]
  const folded = new Set<string>()
  for (const a of hand.actions) {
    if (a.type !== 'fold') continue
    if (STREET_RANK[a.street] > maxRank) continue
    for (const chip of chips) {
      if (actorsMatch(a.actor, chip, hand.heroPosition)) folded.add(chip)
    }
  }
  return folded
}


/**
 * Soft auto-advance after a successful add.
 * Hero → prefer BB (if hero is not BB and BB not folded); else next seat.
 * Seat → next in getPositions (Hero chip for hero seat), wrapping; skip all
 * seats folded on any prior/current street.
 */
export function nextActorAfterAdd(
  acted: string,
  hand: HandState,
  street: Street,
): string {
  const positions = getPositions(hand.playerCount)
  const chips = validActorChips(hand)
  const folded = foldedActorsThroughStreet(hand, street)

  const asChip = (pos: string): string | null => {
    if (pos === hand.heroPosition) {
      // Hero chip is folded if Hero or heroPosition seat folded
      if (folded.has('Hero')) return null
      return 'Hero'
    }
    if (!chips.includes(pos) || folded.has(pos)) return null
    return pos
  }

  if (acted === 'Hero') {
    if (hand.heroPosition !== 'BB') {
      const bb = asChip('BB')
      if (bb) return bb
    }
    const heroIdx = positions.indexOf(hand.heroPosition)
    if (heroIdx >= 0) {
      for (let i = 1; i <= positions.length; i++) {
        const pos = positions[(heroIdx + i) % positions.length]!
        const chip = asChip(pos)
        if (chip) return chip
      }
    }
    return 'Hero'
  }

  const idx = positions.indexOf(acted)
  if (idx < 0) return acted
  for (let i = 1; i <= positions.length; i++) {
    const pos = positions[(idx + i) % positions.length]!
    const chip = asChip(pos)
    if (chip) return chip
  }
  return acted
}

/** Prefer BB for villain call; else first non-hero seat. */
function preferVillainCallSeat(hand: HandState): string {
  const positions = getPositions(hand.playerCount)
  if (hand.heroPosition !== 'BB' && positions.includes('BB')) return 'BB'
  for (const p of positions) {
    if (p !== hand.heroPosition) return p
  }
  return positions[0] ?? 'BB'
}

/** Jump to board and focus the first empty slot for the street. */
export function goToBoardForStreet(street: Street): void {
  const { hand } = state
  let pickTarget: PickTarget = 'flop0'
  if (street === 'flop') {
    if (!hand.board.flop[0]) pickTarget = 'flop0'
    else if (!hand.board.flop[1]) pickTarget = 'flop1'
    else if (!hand.board.flop[2]) pickTarget = 'flop2'
    else pickTarget = 'flop0'
  } else if (street === 'turn') {
    pickTarget = 'turn'
  } else if (street === 'river') {
    pickTarget = 'river'
  } else {
    pickTarget = firstEmptyBoardTarget(hand)
  }
  // Softwise M10b: board cards are asked inline on the Line step.
  void pickTarget
  noteJourneyStep('actions')
  set({ step: 'actions', softGate: null })
}

/** Macro: chronologically honest open then call (folds + Hero raise + call). */
export function applyOpenThenCallMacro(): void {
  const { hand } = state
  const callSeat = preferVillainCallSeat(hand)
  const raiseSize =
    state.actionStreet === 'preflop' && state.actionSize.trim()
      ? state.actionSize.trim()
      : '2.5bb'

  const plan = planOpenThenCall(hand, callSeat, raiseSize)
  if (plan.blocked) {
    showToast('macroOpenExists')
    return
  }

  const orderedPf = buildOpenThenCallOrderedPf(hand, callSeat, raiseSize)
  const currentPf = hand.actions.filter((a) => a.street === 'preflop')
  const alreadyOrdered =
    plan.toAdd.length === 0 &&
    currentPf.length === orderedPf.length &&
    currentPf.every(
      (a, i) =>
        a.type === orderedPf[i]!.type &&
        actorsMatch(a.actor, orderedPf[i]!.actor, hand.heroPosition),
    )
  if (alreadyOrdered) {
    showToast('macroAlreadyLogged')
    return
  }

  const nonPreflopActions = hand.actions.filter((a) => a.street !== 'preflop')
  const nextHand: HandState = {
    ...hand,
    actions: [...orderedPf, ...nonPreflopActions],
  }
  const lastActor = orderedPf[orderedPf.length - 1]!.actor
  const nextSize = defaultDraftForStreet('preflop').actionSize
  const nextActor = nextActorAfterAdd(lastActor, nextHand, 'preflop')
  const partial: Partial<UiState> = {
    actionStreet: 'preflop',
    hand: nextHand,
    actor: nextActor,
    actionType: null,
    actionSize: nextSize,
    editingActionId: null,
  }
  if (!state.sizeSliderUnitManual) {
    partial.sizeSliderUnit = defaultSizeSliderUnit('preflop')
  }
  set(partial)
}

/**
 * Macro (preflop multiway): fold all seats before hero, then focus Hero to open.
 * Safe: skips already-folded seats; aborts if any early non-fold action exists.
 */
export function applyFoldToHeroMacro(): void {
  const { hand } = state
  const plan = planFoldToHero(hand)
  if (plan.blocked) {
    showToast('macroFoldToHeroBlocked')
    return
  }
  if (plan.toFold.length === 0) {
    showToast('macroFoldToHeroNone')
    return
  }
  const toAdd: StreetAction[] = plan.toFold.map((seat) => ({
    id: uid(),
    street: 'preflop',
    actor: seat,
    type: 'fold',
  }))
  const nextHand: HandState = {
    ...hand,
    actions: [...hand.actions, ...toAdd],
  }
  const partial: Partial<UiState> = {
    actionStreet: 'preflop',
    hand: nextHand,
    actor: 'Hero',
    actionType: null,
    actionSize:
      state.actionStreet === 'preflop' && state.actionSize.trim()
        ? state.actionSize.trim()
        : defaultDraftForStreet('preflop').actionSize,
    editingActionId: null,
  }
  if (!state.sizeSliderUnitManual) {
    partial.sizeSliderUnit = defaultSizeSliderUnit('preflop')
  }
  set(partial)
}

/** Bet/raise require size; all-in may optionally carry a size. */
export function actionRequiresSize(type: ActionType): boolean {
  return type === 'bet' || type === 'raise'
}

export function actionAllowsSize(type: ActionType): boolean {
  return type === 'bet' || type === 'raise' || type === 'allin'
}

export function addAction(): void {
  const { actionStreet, actor, actionType, actionSize, hand, editingActionId } =
    state
  const trimmedActor = actor.trim()
  if (!trimmedActor) {
    showToast('actorRequired')
    return
  }
  // Softwise M10a: nothing is added until the user picks an action type.
  if (!actionType) return
  const trimmedSize = actionSize.trim()
  const requires = actionRequiresSize(actionType)
  const allows = actionAllowsSize(actionType)
  if (requires && !trimmedSize) {
    showToast('sizeRequired')
    return
  }
  if (allows && trimmedSize) notePostflopSizeFavorite(trimmedSize)

  const sizeValue = allows && trimmedSize ? trimmedSize : undefined

  // In-place update when editing an existing row
  if (editingActionId) {
    const idx = hand.actions.findIndex((a) => a.id === editingActionId)
    if (idx >= 0) {
      const prev = hand.actions[idx]!
      const updated: StreetAction = {
        ...prev,
        street: actionStreet,
        actor: trimmedActor,
        type: actionType,
        size: sizeValue,
      }
      if (!sizeValue) delete updated.size
      const actions = [...hand.actions]
      actions[idx] = updated
      const nextSize = defaultDraftForStreet(actionStreet).actionSize
      set({
        hand: { ...hand, actions },
        actionSize: nextSize,
        editingActionId: null,
      })
      return
    }
  }

  const entry: StreetAction = {
    id: uid(),
    street: actionStreet,
    actor: trimmedActor,
    type: actionType,
    size: sizeValue,
  }
  // Progressive mastery: sticky type until ≥2 successful Adds, then full street reset.
  // Soft-advance actor always. Edits do not bump the session counter.
  const nextActions = [...hand.actions, entry]
  const nextHand = { ...hand, actions: nextActions }
  const adds = bumpSuccessfulAddCount()
  const nextDraft = draftAfterSuccessfulAdd(actionStreet, actionType, adds)
  const nextActor = nextActorAfterAdd(trimmedActor, nextHand, actionStreet)
  set({
    hand: nextHand,
    actionType: nextDraft.actionType,
    actionSize: nextDraft.actionSize,
    actor: nextActor,
    editingActionId: null,
  })
}

/** Load an action into the draft form for in-place edit. */
export function startEditAction(id: string): void {
  const action = state.hand.actions.find((a) => a.id === id)
  if (!action) return
  const allows = actionAllowsSize(action.type)
  const actionSize = allows ? (action.size ?? '') : ''
  const partial: Partial<UiState> = {
    editingActionId: id,
    actionStreet: action.street,
    actor: action.actor,
    actionType: action.type,
    actionSize,
  }
  if (!state.sizeSliderUnitManual) {
    partial.sizeSliderUnit = defaultSizeSliderUnit(action.street)
  } else if (allows && action.size) {
    const bb = parseBbSize(action.size)
    const pct = parsePctSize(action.size)
    if (bb != null) partial.sizeSliderUnit = 'bb'
    else if (pct != null) partial.sizeSliderUnit = 'pct'
  }
  set(partial)
}

export function cancelEditAction(): void {
  if (!state.editingActionId) return
  const draft = defaultDraftForStreet(state.actionStreet)
  set({
    editingActionId: null,
    actionType: draft.actionType,
    actionSize: draft.actionSize,
  })
}

export function removeAction(id: string): void {
  const clearEdit = state.editingActionId === id
  const nextActions = state.hand.actions.filter((a) => a.id !== id)
  set({
    hand: { ...state.hand, actions: nextActions },
    ...(clearEdit ? { editingActionId: null } : {}),
  })
}

export function resetHand(): void {
  if (toastTimer !== undefined) window.clearTimeout(toastTimer)
  const locale = state.locale
  lastHandLinkPayload = null
  // Soft D Soft-safe ⑤: blank new record only — clear received flag; never import.
  receivedFromLink = false
  clearUndo()
  draftRestorePrompt = false
  // Session hand index: first hand = 1; New hand bumps (unlock Line shop on 2nd).
  // Do not clear add-mastery-count — Adds mastery is session-wide.
  bumpSessionHandCount()
  resetJourneyFarthest()
  heroSeatPicked = false
  settingsReturnStep = 'position'
  state = {
    ...defaultUi(),
    view: state.view === 'privacy' ? 'privacy' : 'app',
    locale,
  }
  // Soft I2a: same locale, no new pick — never persist (e.g. /ko must not write storage).
  setI18nLocale(locale, { persist: false })
  emit()
  // Drop deep-link hash so refresh does not restore the old hand
  if (window.location.hash.startsWith('#/h/')) {
    window.location.hash = '#/'
  }
}

export function openSoftGate(
  intent: SoftGateIntent,
  copyMode?: 'both' | 'human' | 'pro',
): void {
  set({ softGate: { intent, copyMode, phase: 'offer' } })
}

/** Arm the second confirm step before force-sharing an incomplete hand. */
export function armSoftGateForce(): void {
  if (!state.softGate) return
  set({ softGate: { ...state.softGate, phase: 'confirm-force' } })
}

export function dismissSoftGate(): void {
  if (state.softGate) set({ softGate: null })
}

export function acknowledgeSoftGate(): SoftGate {
  const gate = state.softGate
  if (!gate) return null
  set({ softGate: null })
  return gate
}

export function showToast(toast: ToastKind, detail?: string): void {
  if (toastTimer !== undefined) window.clearTimeout(toastTimer)
  toastDetailMemory = toast ? (detail ?? null) : null
  set({ toast })
  if (toast) {
    // Errors need a beat longer to be readable on Actions (no toolbar toast there).
    const ms =
      toast === 'actorRequired' || toast === 'sizeRequired'
        ? 3600
        : toast === 'villainNudge'
          ? 3200
          : toast === 'seatFullName'
            ? 2200
            : toast === 'copied' ||
                toast === 'shared' ||
                toast === 'imageSaved' ||
                toast === 'imageShared' ||
                toast === 'linkCopied'
              ? 2400
              : 1800
    toastTimer = window.setTimeout(() => {
      toastDetailMemory = null
      set({ toast: null })
      toastTimer = undefined
    }, ms)
  }
}


export function syncRouteFromHash(): void {
  const hash = window.location.hash.replace(/^#/, '') || '/'
  if (hash.startsWith('/privacy')) {
    lastHandLinkPayload = null
    if (state.view !== 'privacy') set({ view: 'privacy' })
    return
  }
  if (hash.startsWith('/h/')) {
    const payload = hash.slice(3)
    if (payload && payload !== lastHandLinkPayload) {
      const linked = decodeHandLink(payload)
      if (linked) {
        lastHandLinkPayload = payload
        loadHandFromLink(linked, 'summary')
        return
      }
    }
    if (state.view !== 'app') set({ view: 'app' })
    return
  }
  lastHandLinkPayload = null
  if (state.view !== 'app') set({ view: 'app' })
}

export function navigate(view: ViewId): void {
  const hash = view === 'privacy' ? '#/privacy' : '#/'
  if (window.location.hash !== hash) {
    window.location.hash = hash
  } else {
    setView(view)
  }
}

/** Geo answer kept for this tab session (no refetch / no flip on later loads). */
const GEO_LOCALE_SUFFIX = 'geo-locale'

/** True only on a visit that would ask geo: no session pick, no /ko path, nothing stored. */
export function geoLocaleNeeded(): boolean {
  return !getStoredLocale() && !getSessionLocalePick() && !localeFromPathname()
}

/**
 * First visit: prefer CF geo locale when no stored preference.
 * Does not persist — user language pick still writes holdcard-ex:locale.
 */
export async function hydrateLocaleFromGeo(): Promise<void> {
  if (!geoLocaleNeeded()) return
  let locale: Locale | null = null
  const cached = sessionGet(GEO_LOCALE_SUFFIX)
  if (cached != null) locale = isLocale(cached) ? cached : null
  else {
    const geo = await fetchGeoLocale()
    locale = geo?.locale ?? null
    sessionSet(GEO_LOCALE_SUFFIX, locale ?? '-')
  }
  if (!locale || locale === state.locale) return
  if (!geoLocaleNeeded()) return
  setI18nLocale(locale, { persist: false })
  applyDocumentLocale(locale)
  set({ locale })
}

/** Footer same-page pick: switch in place (no reload), remembered like a footer click. */
export function switchLocaleInPlace(locale: Locale): void {
  rememberFooterLocalePick(locale)
  setI18nLocale(locale)
  applyDocumentLocale(locale)
  set({ locale })
}

// ---------------------------------------------------------------------------
// Softwise M10b — memory-order input (tap-once record, inline street boards)
// ---------------------------------------------------------------------------

/**
 * Seat currently selected to act on the Line step. The user's own pick wins
 * while that seat may still act; otherwise the rule-derived next actor.
 */
export function selectedFlowSeat(hand: HandState = state.hand): string | null {
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind !== 'actions') return null
  const picked = actorSeat(state.actor, hand)
  if (flow.pending.includes(picked)) return picked
  return flow.phase.next
}

/** Pick which seat acts next (skipped seats become "not involved", never folds). */
export function selectFlowSeat(seat: string): void {
  const flow = deriveHandFlow(state.hand)
  if (flow.phase.kind !== 'actions' || !flow.pending.includes(seat)) return
  // Softwise M10f Soft-safe ⑤: cannot jump over a blind poster — they must
  // press 「기록 안 함」 (or act) first. Never auto-mark.
  const si = flow.pending.indexOf(seat)
  for (let i = 0; i < si; i++) {
    const earlier = flow.pending[i]!
    if (flow.unrecordable.includes(earlier)) return
  }
  set({ actor: seatActor(seat, state.hand) })
}

/**
 * Softwise M10f: mark a blind poster 「기록 안 함」 — empty until pressed.
 * Blind stays in the pot; never a fold; never an action in the line.
 * Soft-safe ⑤: never pre-selected.
 */
export function markFlowUnrecorded(seat: string): boolean {
  const hand = state.hand
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind !== 'actions') return false
  if (!flow.pending.includes(seat) || !flow.unrecordable.includes(seat)) return false
  if ((hand.unrecordedSeats ?? []).includes(seat)) return false
  const nextHand: HandState = {
    ...hand,
    unrecordedSeats: [...(hand.unrecordedSeats ?? []), seat],
  }
  const nextFlow = deriveHandFlow(nextHand)
  const partial: Partial<UiState> = {
    hand: nextHand,
    actionType: null,
    actionSize: '2.5bb',
    editingActionId: null,
    actionStreet: nextFlow.street,
  }
  if (nextFlow.phase.kind === 'actions') {
    partial.actor = seatActor(nextFlow.phase.next, nextHand)
  }
  if (handEndsOnePlayerLeft(nextFlow)) {
    set(partial)
    setStep('summary', { card: true })
    return true
  }
  set(partial)
  return true
}

/**
 * Tap-once record: the tapped action is recorded immediately for the selected
 * seat. Only legal actions are accepted; bet/raise need a bb size. Nothing
 * else is ever added — the next actor / street close are derived from rules.
 */
export function recordFlowAction(type: ActionType, sizeBb?: number): boolean {
  const hand = state.hand
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind !== 'actions') return false
  const seat = selectedFlowSeat(hand)
  if (!seat) return false
  const opts = flow.optionsFor(seat)
  if (!opts || !opts.legal.includes(type)) return false
  const entry: StreetAction = {
    id: uid(),
    street: flow.phase.street,
    actor: seatActor(seat, hand),
    type,
  }
  if (type === 'bet' || type === 'raise') {
    if (sizeBb == null || !Number.isFinite(sizeBb) || sizeBb <= opts.minSize) {
      return false
    }
    entry.size = formatBb(sizeBb)
  }
  const nextHand: HandState = { ...hand, actions: [...hand.actions, entry] }
  const nextFlow = deriveHandFlow(nextHand)
  const partial: Partial<UiState> = {
    hand: nextHand,
    actionType: null,
    actionSize: '2.5bb',
    editingActionId: null,
    actionStreet: nextFlow.street,
  }
  if (nextFlow.phase.kind === 'actions') {
    partial.actor = seatActor(nextFlow.phase.next, nextHand)
  }
  bumpSuccessfulAddCount()
  // Softwise M10d: one player left (preflop or any later street) = the hand
  // is over. Go straight to the share card — no 「여기서 끝」 press, no next
  // board card, no opponent-card question. Only the entered actions are kept.
  if (handEndsOnePlayerLeft(nextFlow)) {
    set(partial)
    setStep('summary', { card: true })
    return true
  }
  set(partial)
  return true
}

/** Inline board ask: fill the next empty slot of the street being asked. */
export function assignFlowBoardCard(card: Card): void {
  const hand = state.hand
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind !== 'board') return
  const used = new Set<Card>()
  for (const c of hand.holeCards) if (c) used.add(c)
  for (const h of Object.values(hand.knownHoles ?? {})) {
    if (h) for (const c of h) if (c) used.add(c)
  }
  for (const c of hand.board.flop) if (c) used.add(c)
  if (hand.board.turn) used.add(hand.board.turn)
  if (hand.board.river) used.add(hand.board.river)
  if (used.has(card)) return
  const board = { ...hand.board, flop: [...hand.board.flop] as HandState['board']['flop'] }
  const street = flow.phase.street
  if (street === 'flop') {
    const i = board.flop.findIndex((c) => !c)
    if (i < 0) return
    board.flop[i] = card
  } else if (street === 'turn') {
    board.turn = card
  } else {
    board.river = card
  }
  const nextHand = { ...hand, board }
  const nextFlow = deriveHandFlow(nextHand)
  const partial: Partial<UiState> = { hand: nextHand, actionStreet: nextFlow.street }
  if (nextFlow.phase.kind === 'actions') {
    partial.actor = seatActor(nextFlow.phase.next, nextHand)
  }
  set(partial)
}

/** Clear one flop slot (index) or the turn/river card while it is being asked. */
export function clearFlowBoardSlot(street: 'flop' | 'turn' | 'river', index = 0): void {
  const hand = state.hand
  const board = { ...hand.board, flop: [...hand.board.flop] as HandState['board']['flop'] }
  if (street === 'flop') board.flop[index] = null
  else if (street === 'turn') board.turn = null
  else board.river = null
  set({ hand: { ...hand, board } })
}

const FLOW_STREET_RANK: Record<Street, number> = {
  preflop: 0,
  flop: 1,
  turn: 2,
  river: 3,
}

/**
 * Tap a line segment to jump back: the tapped part and everything recorded
 * after it are cleared so the user re-enters from there. Never adds anything.
 */
export function rewindLineTo(
  target: { actionId: string } | { street: 'flop' | 'turn' | 'river' },
): void {
  const hand = state.hand
  let cutRank: number
  let actions: StreetAction[]
  if ('actionId' in target) {
    const hit = hand.actions.find((a) => a.id === target.actionId)
    if (!hit) return
    cutRank = FLOW_STREET_RANK[hit.street]
    const sameStreet = hand.actions.filter((a) => a.street === hit.street)
    const keepSame = new Set(
      sameStreet.slice(0, sameStreet.indexOf(hit)).map((a) => a.id),
    )
    actions = hand.actions.filter(
      (a) =>
        FLOW_STREET_RANK[a.street] < cutRank ||
        (a.street === hit.street && keepSame.has(a.id)),
    )
    // Boards of later streets go too (they came after this action).
    cutRank += 1
  } else {
    cutRank = FLOW_STREET_RANK[target.street]
    actions = hand.actions.filter((a) => FLOW_STREET_RANK[a.street] < cutRank)
  }
  const board = {
    flop:
      cutRank <= 1
        ? ([null, null, null] as HandState['board']['flop'])
        : ([...hand.board.flop] as HandState['board']['flop']),
    turn: cutRank <= 2 ? null : hand.board.turn,
    river: cutRank <= 3 ? null : hand.board.river,
  }
  // Soft Quiet C2: re-entering clears the mid-cut so asks resume honestly.
  const nextHand: HandState = { ...hand, actions, board, memoryCutAt: undefined }
  const nextFlow = deriveHandFlow(nextHand)
  const partial: Partial<UiState> = {
    hand: nextHand,
    editingActionId: null,
    actionStreet: nextFlow.street,
  }
  if (nextFlow.phase.kind === 'actions') {
    partial.actor = seatActor(nextFlow.phase.next, nextHand)
  }
  set(partial)
}

/**
 * Soft Quiet C2: 「기억 안 남」 — stop further board / actions / villain asks
 * and jump to the share card. Persists `memoryCutAt` so deriveHandFlow is
 * done (reason memory-cut). Never fabricates folds, sizes, board, or result.
 * Soft-safe ⑤: only when pressed.
 */
export function cutFlowMemory(): boolean {
  const hand = state.hand
  if (hand.memoryCutAt != null) return false
  const flow = deriveHandFlow(hand)
  if (flow.phase.kind === 'done') return false
  const nextHand: HandState = { ...hand, memoryCutAt: flow.street }
  const nextFlow = deriveHandFlow(nextHand)
  if (!handEndsMemoryCut(nextFlow)) return false
  set({
    hand: nextHand,
    actionType: null,
    actionSize: '2.5bb',
    editingActionId: null,
    actionStreet: nextFlow.street,
  })
  // Same path as one-left: straight to share — no SoftGate / fabricated fills.
  setStep('summary')
  return true
}
